import { CalendarEvent, EVENT_TYPE_CONFIG } from "@/hooks/useCalendarEvents";
import { Button } from "@/components/ui/button";
import { Lock, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface Props {
  event: CalendarEvent;
  onEdit?: () => void;
}

export const CalendarEventPopover = ({ event, onEdit }: Props) => {
  const navigate = useNavigate();
  const cfg = EVENT_TYPE_CONFIG[event.event_type];

  const dateRange = event.end_date && event.end_date !== event.start_date
    ? `${format(new Date(event.start_date + "T00:00:00"), "MMM d")}–${format(new Date(event.end_date + "T00:00:00"), "MMM d, yyyy")}`
    : format(new Date(event.start_date + "T00:00:00"), "MMM d, yyyy");

  const timeRange =
    !event.all_day && event.start_time
      ? `${event.start_time.slice(0, 5)}${event.end_time ? ` – ${event.end_time.slice(0, 5)}` : ""}`
      : null;

  const goToRecord = () => {
    if (event.linked_wo_id) {
      // Navigate to project with WO context
      if (event.project_id) navigate(`/controller/projects/${event.project_id}`);
    } else if (event.linked_invoice_id) {
      navigate("/controller/invoices");
    }
  };

  const hasLinkedRecord = event.linked_wo_id || event.linked_draw_id || event.linked_invoice_id;

  return (
    <div className="p-3 space-y-2">
      <div className="flex items-start gap-2">
        <span className="h-2.5 w-2.5 rounded-full mt-1 shrink-0" style={{ backgroundColor: cfg.color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <h4 className="text-sm font-semibold truncate">{event.title}</h4>
            {event.is_auto_generated && <Lock className="h-3 w-3 text-muted-foreground shrink-0" />}
          </div>
          <p className="text-xs text-muted-foreground">
            {cfg.label} · {dateRange}
          </p>
          {timeRange && <p className="text-xs text-muted-foreground">{timeRange}</p>}
        </div>
      </div>

      {(event.project_name || event.description) && (
        <div className="border-t pt-2 space-y-1">
          {event.project_name && (
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Project</span>
              <span className="font-medium">{event.project_name}</span>
            </div>
          )}
          {event.description && (
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Notes</span>
              <span className="text-right max-w-[180px] truncate">{event.description}</span>
            </div>
          )}
        </div>
      )}

      <div className="border-t pt-2 flex items-center justify-between">
        {!event.is_auto_generated && onEdit ? (
          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={onEdit}>
            Edit
          </Button>
        ) : (
          <span />
        )}
        {hasLinkedRecord && (
          <Button variant="ghost" size="sm" className="text-xs h-7 gap-1" onClick={goToRecord}>
            Go to Record <ExternalLink className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
};
